<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * "Notify me … before" on an activity being made (D-257): at the time itself, minutes, an
 * hour, a day before, or a time of one's own — several at once. For whoever does it, and for
 * the one making it unless unticked (`remindMe`, shown when they are not the same person).
 * A task with a date and no hour counts from when the person starts work (their profile).
 */
const props = defineProps({
  modelValue: { type: Array, default: () => [] }, // minutes before
  remindMe: { type: Boolean, default: true },
  forSomeoneElse: { type: Boolean, default: false },
  allDay: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'update:remindMe'])
const { t } = useI18n()
const PRESETS = [0, 5, 15, 30, 60, 1440]
const own = ref('')
const ownUnit = ref('60')

const chosen = computed(() => new Set(props.modelValue.map(Number)))
const label = (m) => {
  if (m === 0) return t('reminders.atTime')
  if (m === 1440) return t('reminders.day')
  if (m % 1440 === 0) return t('reminders.days', { n: m / 1440 })
  if (m % 60 === 0) return t('reminders.hours', { n: m / 60 })
  return t('reminders.minutes', { n: m })
}
const extras = computed(() => [...chosen.value].filter((m) => !PRESETS.includes(m)).sort((a, b) => a - b))

function toggle(m) {
  const next = new Set(chosen.value)
  next.has(m) ? next.delete(m) : next.add(m)
  emit('update:modelValue', [...next].sort((a, b) => a - b))
}
function addOwn() {
  const n = Math.round(Number(own.value) * Number(ownUnit.value))
  if (!Number.isFinite(n) || n < 0 || n > 40320 || chosen.value.has(n)) return
  emit('update:modelValue', [...chosen.value, n].sort((a, b) => a - b))
  own.value = ''
}
</script>

<template>
  <div class="space-y-2" data-testid="activity-reminders">
    <p class="label">{{ $t('reminders.title') }}</p>
    <div class="flex flex-wrap gap-1.5">
      <button
        v-for="m in [...PRESETS, ...extras]"
        :key="m"
        type="button"
        :disabled="disabled"
        :aria-pressed="chosen.has(m)"
        :data-minutes="m"
        :class="['rounded-full border px-2.5 py-1 text-xs', chosen.has(m) ? 'border-brand-600 bg-brand-600 text-white' : 'border-default text-muted hover:bg-[var(--surface-3)]']"
        @click="toggle(m)"
      >{{ label(m) }}</button>
    </div>
    <div class="flex flex-wrap items-center gap-1.5 text-xs">
      <input v-model="own" type="number" min="0" class="input w-20 py-1 text-xs" :disabled="disabled" :aria-label="$t('reminders.own')" :placeholder="$t('reminders.own')" @keydown.enter.prevent="addOwn" />
      <select v-model="ownUnit" class="input w-auto py-1 text-xs" :disabled="disabled">
        <option value="1">{{ $t('reminders.unitMinutes') }}</option>
        <option value="60">{{ $t('reminders.unitHours') }}</option>
        <option value="1440">{{ $t('reminders.unitDays') }}</option>
      </select>
      <button type="button" class="link" :disabled="disabled || own === ''" @click="addOwn">{{ $t('reminders.add') }}</button>
    </div>
    <label v-if="forSomeoneElse" class="flex items-center gap-2 text-sm text-muted">
      <input type="checkbox" :checked="remindMe" :disabled="disabled" data-testid="activity-remind-me" @change="emit('update:remindMe', $event.target.checked)" />
      {{ $t('reminders.alsoMe') }}
    </label>
    <p v-if="allDay" class="text-xs text-faint">{{ $t('reminders.allDayHint') }}</p>
  </div>
</template>
